import './gallery.component.scss';
import { createFromObservable } from '@codewyre/wyrekit-solid';
import { useBeforeLeave } from '@solidjs/router';
import {
	BehaviorSubject, concat, debounceTime, distinctUntilChanged, fromEvent, map, of, switchMap
} from 'rxjs';
import {
	For, JSX, onMount, Show
} from 'solid-js';

import { Configuration } from '../../../../../../api/client/configuration';
import { ImageWrapper } from '../../../../components/images/image-wrapper.component';
import { MediaService } from '../../../../services/media.service';
import { SecurityService } from '../../../../services/security.service';
import { UserService } from '../../../../services/user.service';
import { useDependency } from '../../../../shared';

import type { Image } from '../../../../components/images/image.view-model';
import { UserModel } from '../../../../models/user.model';

export function Gallery() {
	const segmentSize = 10;
	const maxThumbSize = 256;

	const configuration = useDependency<Configuration>(Configuration);
	const userService = useDependency<UserService>(UserService);
	const mediaService = useDependency<MediaService>(MediaService);

	useBeforeLeave(e => {
		if (e.from.pathname === (e.to as string).replace(/^([^?]+).*/g, '$1')) {
			return;
		}

		document.body.classList.remove('bg-black');
		document.body.classList.add('d-flex');
		document.body.querySelector('.presentation')?.classList.add('d-flex');
		document.body.querySelector('.presentation')?.classList.remove('h-auto');
	});

	onMount(() => {
		document.body.classList.remove('d-flex');
		document.body.classList.add('bg-black');
		document.body.querySelector('.presentation')?.classList.remove('d-flex');
		document.body.querySelector('.presentation')?.classList.add('h-auto');
	});

	const securityService = useDependency<SecurityService>(SecurityService);

	interface UserGallery {
		isAll: boolean; currentUser: UserModel | null;
	}

	const segments$ = concat(
		of(screen.width),
		fromEvent(window, 'resize')
	)
		.pipe(
			map(() => getSegmentsFor(screen.width))
		);

	const [segments] = createFromObservable(segments$);
	const [cellSize] = createFromObservable(
		segments$.pipe(map(segments => (screen.width - 16) / segments),
			map(targetSize => Math.floor(targetSize)))
	);

	const isAll$ = new BehaviorSubject(true);
	const [isAll] = createFromObservable(isAll$);
	const [user] = createFromObservable(userService.user);
	const [allImages] = createFromObservable(concat(
		of([]),
		mediaService
			.listChangedLastAt
			.pipe(

				// tap(() => setLoading(true)),
				switchMap(() => userService.user),
				switchMap(currentUser => isAll$
					.pipe(distinctUntilChanged())
					.pipe(map(isAll => ({ isAll, currentUser } as UserGallery)))),
				switchMap(ev => concat(of(screen.width),
					fromEvent(window, 'resize'))
					.pipe(debounceTime(200), map(() => ev))),
				switchMap(async ({ isAll, currentUser }) => {
					const segments = getSegmentsFor(screen.width);

					const mediaList = isAll ?
						await mediaService.getAllFiles() :
						await mediaService.getMyFiles(currentUser!);

					return Promise.all(mediaList.map(async media => {
						const width = media.stats.thumbnail?.width || media.stats.original.width!;
						const height = media.stats.thumbnail?.height || media.stats.original.height!;

						const aspectRatio = height / width;
						console.log('Calculating layout for media:', { id: media.id, width, height, aspectRatio });


						const fifteenPercentColumns = Math.ceil(segments * Math.min(0.15, 256 / screen.width * 100 * 0.15));
						const maxColumns = Math.ceil(segments * Math.min(0.6, 256 / screen.width * 100));

						// Calculate Random column count between fifteenPercent and maxColumns, but not more than segments
						const randomColumns = Math.min(Math.ceil(Math.random() * maxColumns) + 1, segments);


						let maxImageColumns = segments / randomColumns + fifteenPercentColumns;

						if (maxImageColumns * segments * segmentSize < width) {
							// If the image is too wide, reduce the maxImageColumns
							maxImageColumns = Math.ceil(segments * segmentSize / width);
						}

						maxImageColumns = Math.min(maxImageColumns, Math.floor(maxThumbSize / segmentSize));

						const rowSpan = Math.round(maxImageColumns * aspectRatio);
						const colSpan = Math.round(rowSpan / aspectRatio);

						return {
							id: media.id,
							createdBy: media.createdBy,
							file: { type: media.type },
							style: {
								'grid-column-end': `span ${colSpan}`,
								'grid-row-end': `span ${rowSpan}`
							} as JSX.CSSProperties,
							dimensions: {
								height, width
							},
							url: Promise.resolve(
								// eslint-disable-next-line @stylistic/js/max-len
								`${configuration.basePath}/media/${media.createdBy.id}/${media.type.startsWith('video') ?
									'stream' :
									'download'}/${media.id}?passcode=${securityService.getPasscode()}`
							),
							thumbUrl: Promise.resolve(
								// eslint-disable-next-line @stylistic/js/max-len
								`${configuration.basePath}/media/${media.createdBy.id}/${media.type.startsWith('video') ?
									'stream' :
									'download'}/${media.id}?type=thumb&passcode=${securityService.getPasscode()}`
							)
						} as Image & {
							dimensions: { height: number; width: number },
							style: Record<string, string>
						};
					}));
				})
			)
	));


	function getSegmentsFor(width: number): number {
		return Math.floor((width - 16) / segmentSize);
	}

	return <div class="d-flex ff-column">
		<div class="gallery" style={{
			'--cell-size': `${cellSize()}px`,
			'--columns': segments()
		}}>
			<For each={allImages()}>
				{image =>
					<>
						<ImageWrapper
							image={image}
							style={image.style}
							isPreview={true}
							dimensions={null}
							images={allImages()} />
					</>}
			</For>
		</div>
		<Show when={!allImages().length}>
			<div class="gallery__empty">
				<div class="gallery__empty__content">
					Noch keine Bilder & Videos verfügbar. Jetzt hochladen!
				</div>
			</div>
		</Show>
		<div class="glass gallery__top-bar d-flex shadow jc-center g-16 ai-center">
			<div
				class={'flex ta-center ' + (isAll() ?
					'active' :
					'')}
				onClick={() => isAll$.next(true)}>Alle Bilder</div>
			<Show when={user()?.id}>
				<div
					class={'flex ta-center ' + (!isAll() ?
						'active' :
						'')}
					onClick={() => isAll$.next(false)}>Meine hochgeladenen</div>
			</Show>
		</div>

		<div class="copyright">
			<span>Erstellt von Marco Klein&nbsp;-&nbsp;</span>
			<a href="https://marcoklein.engineer/" target="_blank" rel="noopener noreferrer">
				marcoklein.engineer
			</a>
		</div>
	</div>;
}
